/* eslint-disable @typescript-eslint/naming-convention */
import {Tooltip} from 'components/Tooltip';
import {MODALS} from 'modules/modals/constants';
import {Portal} from 'modules/portal/components/Portal';
import React, {FC, ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {CityClarificationPopup} from 'modules/locations/components/CityClarificationPopup';
import {isTabletByScreen} from 'core/helpers';
import {CityName} from 'modules/locations/components/CityName';
import {CityList, IProps as ICityListProps} from 'modules/locations/components/CityList';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {selectIsChangeCityLoading, selectIsCityApplied} from 'modules/locations/selectors';
import {changeLocation} from 'modules/locations/thunks';
import style from './style.module.scss';
import {ITypographyPublicProps, Typography} from 'components/Typography';
import classNames from 'classnames';
import {CookieService} from 'core/services/CookieService';
import {CITY_APPLIED_STORE_FLAG} from 'modules/locations/constants';
import {ICity} from 'modules/locations/models';
import {useUserCityId} from 'modules/locations/hooks/useUserCityId';
import Icon from 'components/Icon';

interface IProps {
    className?: string;
    ChevronIcon?: ReactNode;
    isHeaderTopHidden?: boolean;
    textVariant?: ITypographyPublicProps['variants'];
    isCityLetter?: boolean;
    isClarification?: boolean;
    dataAutotestTarget?: string;
    onCityChange?: () => void;
    cityItemTargetPostfix?: string;
    isWineShopList?: boolean;
    testClassName?: string;
}

export const LocationItem: FC<IProps> = ({
    ChevronIcon = <Icon icon={'down'} />,
    isHeaderTopHidden = false,
    className,
    textVariant = {MB: 'p', TB: 'p', DT: 'p'},
    isCityLetter = false,
    isClarification = true,
    dataAutotestTarget,
    onCityChange,
    cityItemTargetPostfix,
    isWineShopList = false,
    testClassName,
}) => {
    const dispatch = useAppDispatch();

    const userCityId = useUserCityId();
    const isCityApplied = useAppSelector(selectIsCityApplied);
    const isChangeCityLoading = useAppSelector(selectIsChangeCityLoading);

    const [isOpenPopup, setIsOpenPopup] = useState<boolean>(false);

    const [selectedCityId, setSelectedCityId] = useState<ICity['id']>();

    useEffect(() => {
        if (userCityId) {
            setSelectedCityId(userCityId);
        }
    }, [userCityId]);

    const handleClosePopup = useCallback(() => {
        setIsOpenPopup(false);
    }, []);

    const handleChooseCity = useCallback(() => {
        setIsOpenPopup(true);
    }, []);

    const handleCitySelect = useCallback<ICityListProps['onSelect']>(
        (cityId) => {
            if (!selectedCityId) {
                return;
            }

            onCityChange?.();

            setSelectedCityId(cityId);

            dispatch(changeLocation(cityId));

            handleClosePopup();
        },
        [dispatch, handleClosePopup, onCityChange, selectedCityId],
    );

    const isClarificationCityPopupHidden = useMemo<boolean>(() => {
        if (isTabletByScreen()) {
            return false;
        }

        const cookieService = CookieService.getInstance();

        return Boolean(null === isCityApplied ? cookieService.get(CITY_APPLIED_STORE_FLAG) : isCityApplied);
    }, [isCityApplied]);

    return (
        <>
            <Tooltip
                childrenClassName={style.locationContainer}
                className={style.locationPopup}
                overlay={
                    <CityList
                        checkedLocationId={selectedCityId}
                        cityItemTargetPostfix={cityItemTargetPostfix}
                        cityNameClassName={style.cityItemName}
                        className={style.popupCityList}
                        dataAutotestTarget={dataAutotestTarget}
                        disabled={isChangeCityLoading}
                        handleClosePopup={handleClosePopup}
                        itemClassName={style.cityItem}
                        onSelect={handleCitySelect}
                    />
                }
                place={'bottom-end'}
                showArrow={!isHeaderTopHidden}
                openOnClick={true}
                isOpen={isOpenPopup}
                setIsOpen={setIsOpenPopup}
            >
                <button
                    className={classNames(
                        style.locationTooltipContainer,
                        'ui-location__switcher js-location-switcher',
                        testClassName,
                    )}
                    data-autotest-target={`${dataAutotestTarget}`}
                    data-autotest-target-id={`${dataAutotestTarget}-dropdown`}
                    data-dropdown-anchor={'city-list-two'}
                    onClick={() => setIsOpenPopup((isOpen) => !isOpen)}
                >
                    <Typography
                        className={classNames(
                            style.itemContainer,
                            className,
                            'location-city-name',
                            'ui-location__switcher-city',
                        )}
                        element={'span'}
                        variants={textVariant}
                    >
                        {isCityLetter && `г. `}
                        <CityName isWineShopList={isWineShopList} />
                    </Typography>
                    <span
                        className={classNames(style.arrowIcon, 'location-arrow-icon', 'ui-location__toggle-arrow', {
                            [style.rotatedArrow]: isOpenPopup,
                        })}
                    >
                        {ChevronIcon}
                    </span>
                </button>
            </Tooltip>
            {isClarification && !isClarificationCityPopupHidden && (
                <Portal name={MODALS.CITY_CLARIFICATION.name} willMount={true}>
                    <CityClarificationPopup
                        chooseAnotherCity={handleChooseCity}
                        containerClassName={classNames(style.clarificationPopup, {
                            [style.top]: isHeaderTopHidden,
                        })}
                    />
                </Portal>
            )}
            <CityList
                checkedLocationId={selectedCityId}
                cityItemTargetPostfix={cityItemTargetPostfix}
                cityNameClassName={style.cityItemName}
                className={style.seoCityList}
                dataAutotestTarget={dataAutotestTarget}
                disabled={isChangeCityLoading}
                handleClosePopup={handleClosePopup}
                itemClassName={style.cityItem}
                onSelect={handleCitySelect}
                hidden={true}
            />
        </>
    );
};
